import { useState, useEffect, useCallback } from 'react';
import { Heart, Plus, Trash2, ArrowRight, Video, Lock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const FONT = { fontFamily: "'Crimson Text', Georgia, serif" };
const ZOOM_LINK = "https://us05web.zoom.us/j/81120587177";

export const PrayerTestimonies = () => {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('prayer');
  const [pw, setPw] = useState('');
  const [pwOpen, setPwOpen] = useState(false);
  const [pwAction, setPwAction] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newText, setNewText] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(async () => {
    try { const r = await fetch(`${API}/api/prayer-items`); if (r.ok) setItems(await r.json()); } catch { toast.error('Unable to load items'); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const prayers = items.filter(i => i.type === 'prayer');
  const testimonies = items.filter(i => i.type === 'testimony');
  const displayed = tab === 'prayer' ? prayers : testimonies;

  const requirePw = (action) => {
    if (pw) { action(); return; }
    setPwAction(() => action);
    setPwOpen(true);
  };

  const handlePwSubmit = () => {
    if (!pw) { toast.error('Please enter a password'); return; }
    setPwOpen(false);
    if (pwAction) pwAction();
  };

  const handleAdd = () => requirePw(() => {
    setNewDate(new Date().toISOString().split('T')[0]);
    setNewText('');
    setAddOpen(true);
  });

  const saveItem = async () => {
    if (!newText.trim()) { toast.error('Please enter the request or testimony'); return; }
    setSaving(true);
    try {
      const body = { date: newDate || new Date().toISOString().split('T')[0], text: newText.trim(), password: pw };
      const res = await fetch(`${API}/api/prayer-items`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) {
        const created = await res.json();
        if (tab === 'testimony' && created.id) {
          await fetch(`${API}/api/prayer-items/${created.id}/to-testimony?password=${pw}`, { method: 'PUT' });
        }
        toast.success('Added!');
        fetchItems();
        setAddOpen(false);
      } else {
        const e = await res.json();
        toast.error(e.detail || 'Failed');
        setPw('');
      }
    } catch { toast.error('Connection error'); } finally { setSaving(false); }
  };

  const moveToTestimony = (item) => requirePw(async () => {
    try {
      const r = await fetch(`${API}/api/prayer-items/${item.id}/to-testimony?password=${pw}`, { method: 'PUT' });
      if (r.ok) { toast.success('Moved to Testimonies — Prayer answered!'); fetchItems(); }
      else { toast.error('Failed'); setPw(''); }
    } catch { toast.error('Error'); }
  });

  const deleteItem = (item) => requirePw(async () => {
    try {
      const r = await fetch(`${API}/api/prayer-items/${item.id}?password=${pw}`, { method: 'DELETE' });
      if (r.ok) { toast.success('Deleted'); fetchItems(); }
      else { toast.error('Failed'); setPw(''); }
    } catch { toast.error('Error'); }
  });

  // Group items by date
  const grouped = {};
  for (const item of displayed) {
    const d = item.date || 'Unknown';
    if (!grouped[d]) grouped[d] = [];
    grouped[d].push(item);
  }
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-800" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <Heart className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
          <h1 className="text-6xl sm:text-7xl md:text-8xl font-bold text-white mb-4">Prayer Requests &amp; Testimonies</h1>
          <p className="text-xl md:text-2xl text-yellow-300 mb-4">Lifting each other up in prayer</p>
          <a href={ZOOM_LINK} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 text-blue-900 rounded-lg font-bold hover:bg-yellow-400 transition-colors text-lg"
            data-testid="zoom-bible-study-link">
            <Video className="h-5 w-5" /> Join Bible Study — Wednesdays at 7 PM via Zoom
          </a>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4">
          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b-2 border-blue-100 pb-3">
            <button onClick={() => setTab('prayer')} data-testid="tab-prayer"
              className={`px-5 py-2 rounded-t-lg font-bold text-lg transition-colors ${tab === 'prayer' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`} style={FONT}>
              Prayer Requests ({prayers.length})
            </button>
            <button onClick={() => setTab('testimony')} data-testid="tab-testimony"
              className={`px-5 py-2 rounded-t-lg font-bold text-lg transition-colors ${tab === 'testimony' ? 'bg-yellow-500 text-blue-900' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'}`} style={FONT}>
              Testimonies ({testimonies.length})
            </button>
            <div className="ml-auto">
              <Button onClick={handleAdd} className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold" style={FONT} data-testid="add-item-btn">
                <Plus className="h-4 w-4 mr-1" /> Add {tab === 'prayer' ? 'Request' : 'Testimony'}
              </Button>
            </div>
          </div>

          {/* Scripture Subtitle */}
          <div className="text-center mb-6">
            {tab === 'prayer' ? (
              <p className="text-blue-600 italic text-lg" style={FONT}>
                &ldquo;I tell you, you can pray for anything, and if you believe that you&apos;ve received it, it will be yours.&rdquo; — Mark 11:24 NLT
              </p>
            ) : (
              <p className="text-yellow-700 italic text-lg" style={FONT}>
                &ldquo;Let them praise the Lord for his great love and for the wonderful things he has done for them.&rdquo; — Psalm 107:31 NLT
              </p>
            )}
          </div>

          {/* Items */}
          {sortedDates.length === 0 ? (
            <p className="text-blue-600 text-center py-12" style={FONT}>
              No {tab === 'prayer' ? 'prayer requests' : 'testimonies'} yet.
            </p>
          ) : (
            <div className="space-y-6">
              {sortedDates.map(date => (
                <div key={date}>
                  <h3 className="text-2xl font-bold text-blue-800 mb-3 border-b border-blue-100 pb-1" style={FONT}>
                    {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </h3>
                  <div className="space-y-2">
                    {grouped[date].map(item => (
                      <div key={item.id} className="flex items-start gap-3 group p-3 rounded-lg hover:bg-blue-50/50 transition-colors">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${tab === 'prayer' ? 'bg-blue-500' : 'bg-yellow-500'}`} />
                        <p className="flex-1 text-blue-900 text-base leading-relaxed" style={FONT}>{item.text}</p>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          {tab === 'prayer' && (
                            <button onClick={() => moveToTestimony(item)} title="Prayer Answered — Move to Testimonies"
                              className="p-1.5 text-green-500 hover:text-green-700 hover:bg-green-50 rounded" data-testid={`move-${item.id}`}>
                              <ArrowRight className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={() => deleteItem(item)} title="Delete"
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`delete-${item.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Password Dialog */}
      <Dialog open={pwOpen} onOpenChange={o => { if (!o) setPwOpen(false); }}>
        <DialogContent className="sm:max-w-sm" data-testid="prayer-pw-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-blue-800"><Lock className="h-5 w-5" /> Admin Access</DialogTitle>
            <DialogDescription style={FONT}>Enter the admin code to continue.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input type="password" data-testid="prayer-pw-input" value={pw} onChange={e => setPw(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handlePwSubmit()} placeholder="Admin code" className="border-2 border-blue-200" style={FONT} />
          </div>
          <DialogFooter>
            <Button onClick={handlePwSubmit} className="bg-blue-700 hover:bg-blue-800 text-white" style={FONT}>Continue</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={o => !o && setAddOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="add-prayer-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-800">Add {tab === 'prayer' ? 'Prayer Request' : 'Testimony'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label style={FONT} className="text-blue-800 font-semibold">Date</Label>
              <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="mt-1 border-2 border-blue-200" style={FONT} data-testid="prayer-date-input" />
            </div>
            <div>
              <Label style={FONT} className="text-blue-800 font-semibold">{tab === 'prayer' ? 'Prayer Request' : 'Testimony'} *</Label>
              <Input value={newText} onChange={e => setNewText(e.target.value)} placeholder={tab === 'prayer' ? 'Enter prayer request...' : 'Enter testimony...'}
                className="mt-1 border-2 border-blue-200" style={FONT} data-testid="prayer-text-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveItem} disabled={saving} className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-bold" style={FONT} data-testid="save-prayer-btn">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
